import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import { getActiveGoal } from '~/storage/repositories/goalRepository'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getProfile } from '~/storage/repositories/profileRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'

/**
 * Whether setup has happened (D-037, amended by D-063 and D-103 §2).
 *
 * **The gate, not the setup.** Writing the records is Project Setup's job —
 * `services/projectSetupService.ts` — and this module deliberately kept only
 * the question the rest of the app asks: has this person been through setup?
 * Three screens ask it, and until the answer is yes, Today shows a setup prompt.
 *
 * `hasActiveProject` joined the set at Checkpoint 2.5. A phase belongs to a
 * project (D-062), and existing installs were given one by the schema v2
 * migration, so requiring it excludes nobody who had completed setup before.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY THIS IS NOT ONE BOOLEAN
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  It used to be `hasProfile && hasActiveProject && hasActivePhase &&
 *  hasActiveGoal`, and a phase or goal missing from that conjunction rendered
 *  the **first-run** copy — "Project Sola needs a few details before it can
 *  interpret anything: your height, your date of birth…" — to someone whose
 *  height and date of birth it was holding at the time.
 *
 *  That was reachable two ways, and both were live:
 *
 *  1. **A cardio-only user, from the moment they finished setup.** Since D-090
 *     the phase belongs to the body-composition domain goal, and setup writes no
 *     phase and no `GoalRecord` when body composition is inactive. So completing
 *     setup left `phases: 0, goals: 0` and the app immediately asked them to set
 *     up again. Proven in a browser on 2026-08-10.
 *  2. **Any user whose phase ended**, which is D-103 §2's finding.
 *
 *  In both cases the offered escape is "Set up Project Sola", which runs setup
 *  a second time and writes a second active project — the fork D-103 §7 closes.
 *
 *  **So the phase and the goal are facts about the body-composition domain, not
 *  conditions of having been set up.** They stay on this record because screens
 *  legitimately ask about them; what they no longer do is decide whether the
 *  user is a stranger. Nothing downstream needed changing to allow it: the
 *  engine already answers `contextUnavailable` with no phase, `combineDomainOpinions`
 *  already returns `nothingToSay` with the reason, and both pages already render
 *  the phase line conditionally.
 */
export type OnboardingStatus =
  /** No profile or no active project: this person has not been through setup. */
  | 'firstRun'
  /** Set up. Says nothing about whether a phase exists — read `hasActivePhase`. */
  | 'ready'

export interface OnboardingState {
  readonly status: OnboardingStatus
  readonly hasProfile: boolean
  readonly hasActiveProject: boolean
  /**
   * Whether body composition has a running phase.
   *
   * `false` for a user who never activated the domain and for one whose phase
   * has ended. **Not a setup condition** — see the note above.
   */
  readonly hasActivePhase: boolean
  /** Whether a body-composition `GoalRecord` exists. Not a setup condition. */
  readonly hasActiveGoal: boolean
}

export async function getOnboardingState(
  db: SolaDatabase,
): Promise<Result<OnboardingState, StorageError>> {
  const profile = await getProfile(db)
  if (!profile.ok) return profile

  const project = await getActiveProject(db)
  if (!project.ok) return project

  const phase = await getActivePhase(db)
  if (!phase.ok) return phase

  const goal = await getActiveGoal(db)
  if (!goal.ok) return goal

  const hasProfile = profile.value !== undefined
  const hasActiveProject = project.value !== undefined
  const hasActivePhase = phase.value !== undefined
  const hasActiveGoal = goal.value !== undefined

  return ok({
    /* Profile AND project, not either: a phase or goal with no project behind
       it is the orphaned history schema v2 exists to eliminate (D-062), and it
       must read as first-run rather than let the app run against it. */
    status: hasProfile && hasActiveProject ? 'ready' : 'firstRun',
    hasProfile,
    hasActiveProject,
    hasActivePhase,
    hasActiveGoal,
  })
}

export { storageError }
